import { avatarNone } from '../../icons'
import { focus, svgColor, svgUri } from '../../helpers'
import { StyleFn } from '..'

const IconEdit = {
  data: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.0.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path d="M416.9 85.2L372 130.1L509.9 268L554.8 223.1C568.4 209.6 576 191.2 576 172C576 152.8 568.4 134.4 554.8 120.9L519.1 85.2C505.6 71.6 487.2 64 468 64C448.8 64 430.4 71.6 416.9 85.2zM338.1 164L122.9 379.1C112.2 389.8 104.4 403.2 100.3 417.8L64.9 545.6C62.6 553.9 64.9 562.9 71.1 569C77.3 575.1 86.2 577.5 94.5 575.2L222.3 539.7C236.9 535.6 250.2 527.9 261 517.1L476 301.9L338.1 164z"/></svg>',
}

const IconChatbot = {
  data: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M3 5V20.7929C3 21.2383 3.53857 21.4614 3.85355 21.1464L7.70711 17.2929C7.89464 17.1054 8.149 17 8.41421 17H19C20.1046 17 21 16.1046 21 15V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M15 12C14.2005 12.6224 13.1502 13 12 13C10.8498 13 9.79952 12.6224 9 12" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M9 8.01953V8" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M15 8.01953V8" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>',
}

export const Avatar: StyleFn = (theme) => {
  const { color, size } = theme
  const { focusWithPseudo } = focus(theme)

  const statusStyles = {
    content: '""',
    display: 'block',
    position: 'absolute',
    right: 2,
    bottom: 2,
    minWidth: '22%',
    minHeight: '22%',
    border: `2px solid ${color.palette.white}`,
    borderRadius: '50%',
  }
  const focusColor = color.getModalValues(color.border.focus)
  const iconColor = color.getModalValues(color.background.inverse)

  return {
    root: {
      extend: [focusWithPseudo({ radius: '50%', pseudo: 'before' })],
      display: 'flex',
      width: '1em',
      height: '1em',
      fontSize: size.spacing[48],
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      borderRadius: '50%',
      position: 'relative',
      '&:hover,&:active': {
        border: `1px solid ${focusColor}`,
      },
    },
    img: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
    },
    _editable: {
      position: 'relative',
      cursor: 'pointer',
      '&:hover:after': {
        content: '""',
        borderRadius: '50%',
        // percentage is applied to background size to scale with increase in fontsize
        position: 'absolute',
        width: '100%',
        height: '100%',
        opacity: 0.8,
        backgroundColor: color.palette.primaryblue[90],
        backgroundImage: svgUri(svgColor(IconEdit.data, '#fff')),
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '56%',
      },
    },
    _none: {
      borderRadius: '50%',
      backgroundColor: color.palette.warmgrey['30'],
      backgroundImage: svgUri(avatarNone(iconColor)),
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    },
    _chatbot: {
      backgroundColor: color.palette.warmgrey['30'],
      backgroundImage: svgUri(svgColor(IconChatbot.data, color.palette.primaryblue[90])),
      backgroundPosition: 'center center',
      borderRadius: '50%',
      color: iconColor,
    },
    _notDisturb: {
      '&:after': {
        extend: [statusStyles],
        backgroundColor: color.feedback.error.default,
      },
    },
    _away: {
      '&:after': {
        extend: [statusStyles],
        backgroundColor: color.feedback.warning.default,
      },
    },
    _offline: {
      '&:after': {
        extend: [statusStyles],
        backgroundColor: color.palette.coolgrey['50'],
      },
    },
    _online: {
      '&:after': {
        extend: [statusStyles],
        backgroundColor: color.feedback.success.default,
      },
    },
    _xsmall: {
      fontSize: size.spacing[24],
      '&:after': {
        width: size.spacing[6],
        height: size.spacing[6],
        right: 0,
        borderWidth: 1,
      },
    },
    _small: {
      fontSize: size.spacing[32],
      '&:after': {
        width: size.spacing[8],
        height: size.spacing[8],
        right: 0,
        borderWidth: 1,
      },
    },
    _medium: {
      '&:after': {
        width: size.spacing[12],
        height: size.spacing[12],
        right: 0,
      },
    },
    _large: {
      fontSize: size.spacing[64],
      '&:after': {
        width: `calc(${size.spacing[16]} - 1px)`,
        height: `calc(${size.spacing[16]} - 1px)`,
        right: 1,
      },
    },
    _xlarge: {
      fontSize: size.spacing[80],
      '&:after': {
        width: size.spacing[18],
        height: size.spacing[18],
      },
    },
    _xxlarge: {
      fontSize: size.spacing[112],
      '&:after': {
        width: size.spacing[24],
        height: size.spacing[24],
      },
    },
  }
}
